import { useEffect, useMemo } from "react";

import { Alert } from "../../../components/ui/Alert";
import { AgendaAlertController } from "../../../services/controllers/global";
import { parseInlineMarkdown } from "../../../services/functions";
import { useAgendaAlerts } from "../../../store/global/useAgendaAlerts";

const ALERT_NAME = "agenda-alerts";

const alertTypeConfig = {
  info: {
    label: "Informação",
    titleClass: "bg-scale-500",
    buttonClass: "!bg-scale-500 hover:!brightness-95 focus:!ring-scale-500",
  },
  warning: {
    label: "Atenção",
    titleClass: "bg-warning",
    buttonClass: "!bg-warning hover:!brightness-95 focus:!ring-warning",
  },
  critical: {
    label: "Crítico",
    titleClass: "bg-error",
    buttonClass: "!bg-error hover:!brightness-95 focus:!ring-error",
  },
};

function renderParagraph(lines, key) {
  return (
    <p key={key} className="text-sm leading-6 text-gray-700">
      {lines.map((line, index) => (
        <span key={`${key}-${index}`}>
          {index > 0 && <br />}
          {parseInlineMarkdown(line, `${key}-${index}`)}
        </span>
      ))}
    </p>
  );
}

function renderList(lines, ordered, key) {
  const List = ordered ? "ol" : "ul";

  return (
    <List
      key={key}
      className={`text-sm leading-6 text-gray-700 ${ordered ? "list-decimal" : "list-disc"} pl-5 space-y-1`}
    >
      {lines.map((line, index) => (
        <li key={`${key}-${index}`}>
          {parseInlineMarkdown(line, `${key}-${index}`)}
        </li>
      ))}
    </List>
  );
}

function MarkdownContent({ content }) {
  const blocks = useMemo(() => {
    const lines = `${content || ""}`.replace(/\r\n/g, "\n").split("\n");
    const renderedBlocks = [];
    let index = 0;

    while (index < lines.length) {
      const line = lines[index];

      if (!line.trim()) {
        index += 1;
        continue;
      }

      const unorderedMatch = line.match(/^\s*[-*+]\s+(.+)$/);
      const orderedMatch = line.match(/^\s*\d+[.)]\s+(.+)$/);
      const headingMatch = line.match(/^\s{0,3}#{1,4}\s+.+$/);
      const imageMatch = line.match(/^\s*!\[[^\]]*\]\([^)]+\)\s*$/);

      if (headingMatch) {
        renderedBlocks.push(parseInlineMarkdown(line, `heading-${index}`));
        index += 1;
        continue;
      }

      if (imageMatch) {
        renderedBlocks.push(parseInlineMarkdown(line.trim(), `image-${index}`));
        index += 1;
        continue;
      }

      if (unorderedMatch || orderedMatch) {
        const ordered = Boolean(orderedMatch);
        const listLines = [];

        while (index < lines.length) {
          const currentLine = lines[index];
          const currentMatch = ordered
            ? currentLine.match(/^\s*\d+[.)]\s+(.+)$/)
            : currentLine.match(/^\s*[-*+]\s+(.+)$/);

          if (!currentMatch) break;

          listLines.push(currentMatch[1]);
          index += 1;
        }

        renderedBlocks.push(renderList(listLines, ordered, `list-${index}`));
        continue;
      }

      const paragraphLines = [];

      while (index < lines.length) {
        const currentLine = lines[index];
        const isListLine = /^\s*[-*+]\s+/.test(currentLine) || /^\s*\d+[.)]\s+/.test(currentLine);
        const isHeadingLine = /^\s{0,3}#{1,4}\s+.+$/.test(currentLine);
        const isImageLine = /^\s*!\[[^\]]*\]\([^)]+\)\s*$/.test(currentLine);

        if (!currentLine.trim() || isListLine || isHeadingLine || isImageLine) break;

        paragraphLines.push(currentLine);
        index += 1;
      }

      renderedBlocks.push(renderParagraph(paragraphLines, `paragraph-${index}`));
    }

    return renderedBlocks;
  }, [content]);

  return <div className="space-y-3">{blocks}</div>;
}

export function AgendaAlerts() {
  const controller = AgendaAlertController();
  const { alerts, currentIndex, dismissing } = useAgendaAlerts((state) => state.data);
  const currentAlert = alerts[currentIndex];
  const config = alertTypeConfig[currentAlert?.type] || alertTypeConfig.info;

  useEffect(() => {
    controller.init();
  }, [controller]);

  useEffect(() => {
    Alert.store.getState().actions.isOpen(ALERT_NAME, Boolean(currentAlert));
  }, [currentAlert]);

  if (!currentAlert) return null;

  const handleDismiss = () => {
    controller.dismissCurrentAlert();
  };

  return (
    <Alert.Root name={ALERT_NAME}>
      <Alert.Content
        noScroll
        noPadding
        className="!max-h-[90vh] !max-w-[600px] !items-stretch !rounded-lg !bg-white !outline-none ring-0 [&_*]:!outline-none [&_*]:!ring-0"
      >
        <div className="flex max-h-[90vh] w-full flex-col overflow-hidden">
          <Alert.Title variant="bar" className={`flex items-center justify-center text-center ${config.titleClass}`}>
            {currentAlert.title}
          </Alert.Title>
          <Alert.Description className="sr-only !m-0">
            Alerta global da Agenda: {config.label}
          </Alert.Description>

          <div className="flex-1 overflow-auto px-10 p-8">
            <MarkdownContent content={currentAlert.content} />
          </div>

          <Alert.Actions className="!mt-0 !justify-end border-t border-gray-200 bg-white px-4 py-3">
            <Alert.Confirm
              type="button"
              onClick={handleDismiss}
              disabled={dismissing}
              full
              className={`!max-w-[150px] !rounded-md !px-5 !py-2 !text-sm !font-bold !text-white transition focus:!outline-none focus:!ring-2 focus:!ring-offset-2 disabled:!cursor-not-allowed disabled:!bg-gray-300 disabled:!text-white ${config.buttonClass}`}
            >
              Entendi
            </Alert.Confirm>
          </Alert.Actions>
        </div>
      </Alert.Content>
    </Alert.Root>
  );
}
